import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { AlertTriangle, X } from "lucide-react";
import {
  validateSchemaAgainstWidgets,
  type SchemaWidgetValidation,
} from "@/lib/napAudit";

const DISMISS_KEY = "nap-schema-drift-dismissed-at";

/**
 * Visible production-safe warning that fires when the page's clinic JSON-LD
 * stops matching the values rendered inside the header/footer NAP widgets.
 *
 * - Runs after every route change (waits one tick so JSON-LD has been mounted).
 * - Stays silent when everything aligns.
 * - Logs a structured warning to the console for observability tools.
 * - Dismissible for 24h via localStorage so it doesn't nag during a deploy.
 */
const NapSchemaDriftWarning = () => {
  const location = useLocation();
  const [validation, setValidation] = useState<SchemaWidgetValidation | null>(
    null,
  );
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const t = window.setTimeout(() => {
      const v = validateSchemaAgainstWidgets();
      setValidation(v);
      if (!v.ok && v.hasWidget) {
        // eslint-disable-next-line no-console
        console.warn("[NAP] JSON-LD drift detected vs header/footer", {
          url: window.location.pathname,
          drift: v.drift,
          fields: v.fields.filter((f) => !f.ok),
        });
      }

      // Honour a fresh dismissal for 24h.
      const dismissedAt = Number(
        window.localStorage.getItem(DISMISS_KEY) ?? 0,
      );
      const oneDay = 24 * 60 * 60 * 1000;
      setDismissed(Boolean(dismissedAt) && Date.now() - dismissedAt < oneDay);
    }, 250);
    return () => window.clearTimeout(t);
  }, [location.pathname, location.search]);

  if (!validation || validation.ok || !validation.hasWidget || dismissed) {
    return null;
  }

  return (
    <div
      role="alert"
      aria-live="polite"
      data-embed-hide="true"
      className="fixed top-4 right-4 z-[80] max-w-sm rounded-xl border border-amber-300 bg-amber-50 text-amber-950 shadow-xl"
    >
      <div className="flex items-start gap-3 p-4">
        <AlertTriangle className="h-5 w-5 mt-0.5 flex-shrink-0 text-amber-700" />
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-[0.25em] font-semibold text-amber-800">
            NAP schema drift
          </p>
          <p className="text-sm font-medium mt-1">
            Your structured data no longer matches the header/footer.
          </p>
          <ul className="mt-2 space-y-1 text-xs leading-snug">
            {validation.drift.slice(0, 4).map((line) => (
              <li key={line} className="break-words">
                • {line}
              </li>
            ))}
          </ul>
          <p className="text-[11px] mt-2 opacity-80">
            Update <span className="font-mono">src/lib/clinic.ts</span> so
            JSON-LD and the visible NAP agree, then redeploy.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            window.localStorage.setItem(DISMISS_KEY, String(Date.now()));
            setDismissed(true);
          }}
          aria-label="Dismiss for 24 hours"
          className="text-amber-700 hover:text-amber-900 transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default NapSchemaDriftWarning;
